<script setup lang="ts">
import { useLocalStorage } from '@vueuse/core'

import type { ScoreboardTeamStructure, ScoreboardTimerMode } from '~/stores/scoreboard'

import {
  SCOREBOARD_BOYS_GIRLS_TEAMS,
  SCOREBOARD_COLOR_TEAMS,
  SCOREBOARD_CUSTOM_COLORS,
  SCOREBOARD_MAX_CUSTOM_TEAMS,
} from '~/stores/scoreboard'

const props = withDefaults(defineProps<{
  pairingCode?: string
  pairingUrl?: string
  peerPresentersCount?: number
  peerReady?: boolean
  popupPresenterConnected?: boolean
  presentationConnecting?: boolean
  presentationSupported?: boolean
  presenterEntryUrl?: string
}>(), {
  pairingCode: '',
  pairingUrl: '',
  peerPresentersCount: 0,
  peerReady: false,
  popupPresenterConnected: false,
  presentationConnecting: false,
  presentationSupported: true,
  presenterEntryUrl: '',
})

const emit = defineEmits<{
  connectPresentationWindow: []
  disconnectPeerPresenters: []
  disconnectPresentation: []
  launchDisplayMode: []
  regeneratePairingCode: []
  start: []
}>()

const store = useScoreboardStore()
const { data: ui } = useScoreboardUI()

// ── Option lists driven by content ──────────────────────
const teamStructures = computed(() => ui.value?.setup.teamStructure.structures ?? [])
const teamNameLabel = computed(() => ui.value?.setup.teamStructure.teamNameLabel ?? 'Team')
const timerModes = computed(() => ui.value?.setup.timer.modes ?? [])
const durationPresets = computed(() => ui.value?.setup.timer.durationPresets ?? [])

// ── Persisted setup options ─────────────────────────────
const teamStructure = useLocalStorage<ScoreboardTeamStructure>('sfg.scoreboard.teamStructure', 'color-teams')
const customTeamCount = useLocalStorage<number>('sfg.scoreboard.customTeamCount', 4)
const customTeamNames = useLocalStorage<string[]>(
  'sfg.scoreboard.customTeamNames',
  Array.from({ length: SCOREBOARD_MAX_CUSTOM_TEAMS }, (_, i) => `${teamNameLabel.value} ${i + 1}`),
)
const timerMode = useLocalStorage<ScoreboardTimerMode>('sfg.scoreboard.timerMode', 'none')
const timerDurationSeconds = useLocalStorage<number>('sfg.scoreboard.timerDurationSeconds', 30)
const autoStopOnScore = useLocalStorage<boolean>('sfg.scoreboard.autoStopOnScore', true)

// Custom mode is explicit state, not derived. Picking Custom reveals
// the input even when the persisted duration happens to match a preset
// (e.g. a leader who typed in 30 manually still gets the input back
// after a reload). Picking any preset switches the mode back.
const isCustomDurationActive = ref<boolean>(
  !durationPresets.value.some(p => p.seconds === timerDurationSeconds.value),
)
// Mirrors the input field while it's open so a half-typed value stays
// visible until the leader commits with blur or Enter.
const customDurationInput = ref<number | string>(timerDurationSeconds.value)

// On first hydration the persisted duration may not match any preset
// (saved custom value); flip the explicit flag so the input is visible
// for the leader to confirm or change.
watch(durationPresets, presets => {
  if (presets.length > 0 && !presets.some(p => p.seconds === timerDurationSeconds.value)) {
    isCustomDurationActive.value = true
  }
}, { immediate: true })

// Up to 8 teams in custom mode; the count buttons render 1..8.
const teamCountOptions = Array.from(
  { length: SCOREBOARD_MAX_CUSTOM_TEAMS },
  (_, i) => i + 1,
)

const colorTeamsPreview = SCOREBOARD_COLOR_TEAMS
const boysGirlsPreview = SCOREBOARD_BOYS_GIRLS_TEAMS

function activateCustomDuration() {
  isCustomDurationActive.value = true
  // Seed the input with whatever the persisted duration is so the
  // leader sees a sensible number to edit. Clamp into the input's
  // accepted range up front.
  customDurationInput.value = Math.min(600, Math.max(1, timerDurationSeconds.value))
}

function applyCustomDuration() {
  const n = Number(customDurationInput.value)
  if (Number.isFinite(n) && n > 0) {
    // Clamp custom durations to [1, 600] (10 min) so a typo doesn't
    // produce a wildly broken timer state. If the leader needs longer
    // they can use count-up mode.
    timerDurationSeconds.value = Math.min(600, Math.max(1, Math.round(n)))
    customDurationInput.value = timerDurationSeconds.value
  }
}

// ── Reset handler ────────────────────────────────────────
function handleReset() {
  teamStructure.value = 'color-teams'
  customTeamCount.value = 4
  customTeamNames.value = Array.from({ length: SCOREBOARD_MAX_CUSTOM_TEAMS }, (_, i) => `${teamNameLabel.value} ${i + 1}`)
  timerMode.value = 'none'
  timerDurationSeconds.value = 30
  customDurationInput.value = 30
  isCustomDurationActive.value = false
  autoStopOnScore.value = true
}

// ── Helpers ──────────────────────────────────────────────
function pickPreset(seconds: number) {
  timerDurationSeconds.value = seconds
  customDurationInput.value = seconds
  isCustomDurationActive.value = false
}

defineExpose({ handleReset })

// ── Start handler ────────────────────────────────────────
function handleStart() {
  // Make sure any half-typed custom duration is committed before we
  // hand the value to the store, otherwise the leader could end up
  // playing with the previous preset.
  if (timerMode.value === 'countdown' && isCustomDurationActive.value) {
    applyCustomDuration()
  }
  store.config.teamStructure = teamStructure.value
  store.config.customTeamCount = customTeamCount.value
  store.config.customTeamNames = [ ...customTeamNames.value ]
  store.config.timerMode = timerMode.value
  store.config.timerDurationSeconds = timerDurationSeconds.value
  store.config.autoStopOnScore = autoStopOnScore.value
  emit('start')
}
</script>

<template>
  <div class="space-y-8">
    <!-- ── Team Structure ─────────────────────────────────── -->
    <UCard :ui="{ header: 'bg-muted/50', body: 'bg-muted/10' }">
      <template #header>
        <div class="flex items-center gap-3">
          <UIcon
            class="text-primary size-9 shrink-0"
            name="mdi:account-group-outline"
          />
          <div>
            <h2 class="text-lg font-semibold">
              {{ ui?.setup.teamStructure.title }}
            </h2>
            <p class="text-sm text-muted">
              {{ ui?.setup.teamStructure.description }}
            </p>
          </div>
        </div>
      </template>

      <div class="space-y-4">
        <!-- Structure selector -->
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <SfgOptionCard
            v-for="ts in teamStructures"
            :key="ts.value"
            :selected="teamStructure === ts.value"
            @click="teamStructure = ts.value"
          >
            <div class="px-4 py-3">
              <div class="font-semibold">
                {{ ts.label }}
              </div>
              <div class="mt-0.5 text-xs text-brand-ink/70">
                {{ ts.description }}
              </div>
            </div>
          </SfgOptionCard>
        </div>

        <!-- Color Teams preview -->
        <div
          v-if="teamStructure === 'color-teams'"
          class="flex flex-wrap gap-2 pt-1"
        >
          <div
            v-for="team in colorTeamsPreview"
            :key="team.name"
            :style="{ backgroundColor: team.color }"
            class="rounded-lg px-4 py-2 text-sm font-bold text-white shadow"
          >
            {{ team.name }}
          </div>
        </div>

        <!-- Boys v Girls preview -->
        <div
          v-else-if="teamStructure === 'boys-v-girls'"
          class="flex flex-wrap gap-2 pt-1"
        >
          <div
            v-for="team in boysGirlsPreview"
            :key="team.name"
            :style="{ backgroundColor: team.color }"
            class="rounded-lg px-4 py-2 text-sm font-bold text-white shadow"
          >
            {{ team.name }}
          </div>
        </div>

        <!-- Custom options -->
        <div
          v-else-if="teamStructure === 'custom'"
          class="space-y-4 pt-2"
        >
          <!-- Team count -->
          <div class="flex flex-wrap items-center gap-3">
            <span class="min-w-32 text-sm font-medium text-muted">{{ ui?.setup.teamStructure.numberOfTeamsLabel }}</span>
            <div class="flex flex-wrap gap-2">
              <SfgOptionCard
                v-for="n in teamCountOptions"
                :key="n"
                :selected="customTeamCount === n"
                class="w-auto!"
                @click="customTeamCount = n"
              >
                <span class="block px-4 py-2 text-sm font-semibold tabular-nums">{{ n }}</span>
              </SfgOptionCard>
            </div>
          </div>

          <!-- Team name inputs -->
          <div class="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <UFormField
              v-for="i in customTeamCount"
              :key="i"
              :label="`${teamNameLabel} ${i}`"
            >
              <div class="flex items-center gap-2">
                <span
                  :style="{ backgroundColor: SCOREBOARD_CUSTOM_COLORS[(i - 1) % SCOREBOARD_CUSTOM_COLORS.length] }"
                  class="size-3 shrink-0 rounded-full"
                />
                <UInput
                  v-model="customTeamNames[i - 1]"
                  :placeholder="`${teamNameLabel} ${i}`"
                  class="w-full"
                />
              </div>
            </UFormField>
          </div>
        </div>
      </div>
    </UCard>

    <!-- ── Timer ──────────────────────────────────────────── -->
    <UCard :ui="{ header: 'bg-muted/50', body: 'bg-muted/10' }">
      <template #header>
        <div class="flex items-center gap-3">
          <UIcon
            class="text-primary size-9 shrink-0"
            name="mdi:timer-outline"
          />
          <div>
            <h2 class="text-lg font-semibold">
              {{ ui?.setup.timer.title }}
            </h2>
            <p class="text-sm text-muted">
              {{ ui?.setup.timer.description }}
            </p>
          </div>
        </div>
      </template>

      <div class="space-y-5">
        <!-- Mode selector -->
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <SfgOptionCard
            v-for="mode in timerModes"
            :key="mode.value"
            :selected="timerMode === mode.value"
            @click="timerMode = mode.value"
          >
            <div class="flex items-start gap-3 px-4 py-3">
              <UIcon
                :name="mode.icon"
                class="size-7 shrink-0 mt-0.5"
              />
              <div class="min-w-0">
                <div class="font-semibold">
                  {{ mode.label }}
                </div>
                <div class="mt-0.5 text-xs text-brand-ink/70">
                  {{ mode.description }}
                </div>
              </div>
            </div>
          </SfgOptionCard>
        </div>

        <!-- Countdown duration picker -->
        <div v-if="timerMode === 'countdown'" class="space-y-3">
          <h3 class="text-sm font-medium text-muted">
            {{ ui?.setup.timer.durationTitle }}
          </h3>
          <div class="flex flex-wrap gap-2">
            <SfgOptionCard
              v-for="preset in durationPresets"
              :key="preset.seconds"
              :selected="!isCustomDurationActive && timerDurationSeconds === preset.seconds"
              class="w-auto!"
              @click="pickPreset(preset.seconds)"
            >
              <span class="block px-4 py-2 text-sm font-semibold tabular-nums">{{ preset.label }}</span>
            </SfgOptionCard>
            <SfgOptionCard
              :selected="isCustomDurationActive"
              class="w-auto!"
              @click="activateCustomDuration"
            >
              <span class="block px-4 py-2 text-sm font-semibold">{{ ui?.gameScreen.customDurationButtonLabel }}</span>
            </SfgOptionCard>
          </div>
          <UFormField
            v-if="isCustomDurationActive"
            :label="ui?.setup.timer.customDurationLabel"
          >
            <UInput
              v-model.number="customDurationInput"
              :min="1"
              :max="600"
              type="number"
              class="w-32"
              @blur="applyCustomDuration"
              @keydown.enter="applyCustomDuration"
            />
          </UFormField>
        </div>

        <!-- Auto-stop toggle (only relevant when a timer is active) -->
        <div
          v-if="timerMode !== 'none'"
          class="space-y-2"
        >
          <h3 class="text-sm font-medium text-muted">
            {{ ui?.setup.timer.autoStopTitle }}
          </h3>
          <div class="flex items-start gap-3">
            <USwitch
              v-model="autoStopOnScore"
              :ui="{ root: 'mt-0.5 shrink-0' }"
            />
            <p class="text-sm text-muted">
              {{ autoStopOnScore ? ui?.setup.timer.autoStopOnLabel : ui?.setup.timer.autoStopOffLabel }}
            </p>
          </div>
        </div>
      </div>
    </UCard>

    <!-- ── Presentation Screen ──────────────────────────── -->
    <GamePresentationCard
      :pairing-code="props.pairingCode"
      :pairing-url="props.pairingUrl"
      :peer-presenters-count="props.peerPresentersCount"
      :peer-ready="props.peerReady"
      :popup-presenter-connected="props.popupPresenterConnected"
      :presentation-connecting="props.presentationConnecting"
      :presentation-supported="props.presentationSupported"
      :presenter-entry-url="props.presenterEntryUrl"
      @connect-presentation-window="emit('connectPresentationWindow')"
      @disconnect-peer-presenters="emit('disconnectPeerPresenters')"
      @disconnect-presentation="emit('disconnectPresentation')"
      @launch-display-mode="emit('launchDisplayMode')"
      @regenerate-pairing-code="emit('regeneratePairingCode')"
    />

    <!-- ── Start Button ──────────────────────────────────── -->
    <div class="flex flex-col items-center gap-3 pt-2">
      <UButton
        icon="mdi:play-circle"
        :label="ui?.setup.startButton"
        size="xl"
        @click="handleStart"
      />
    </div>
  </div>
</template>
